import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api } from '../lib/api'
import { useKnowledgeStore } from '../lib/knowledgeStore'
import { formatUploaded, groupSources } from './KnowledgeView'

/**
 * The knowledge right-column panel. It shows the source the user selected in the indexed-sources
 * list — its honest metadata from `GET /v1/sources` (category, format, chunk count, uploader,
 * date) — and, when a search has been run, previews the actual chunks that source matched in the
 * ACL-trimmed `/v1/ask` retrieval. Both come from the same TanStack Query caches the
 * `KnowledgeView` populates (`['sources', bid]` and `['knowledge', bid, query]`), so no extra
 * fetch. It only ever shows data the server actually returned.
 */
export function SourceInspector({ bid }: { bid: string | null }) {
  const { t } = useLang()
  const submittedQuery = useKnowledgeStore((s) => s.submittedQuery)
  const selectedSourceId = useKnowledgeStore((s) => s.selectedSourceId)

  const sourcesQ = useQuery({
    queryKey: ['sources', bid],
    queryFn: () => api.listSources(bid as string),
    enabled: Boolean(bid),
  })
  const results = useQuery({
    queryKey: ['knowledge', bid, submittedQuery],
    queryFn: () => api.ask(bid as string, submittedQuery),
    enabled: Boolean(bid) && submittedQuery.length > 0,
  })

  const listSource = sourcesQ.data?.sources.find((s) => s.id === selectedSourceId) ?? null
  const matches = useMemo(() => groupSources(results.data), [results.data])
  const matched = matches.find((m) => m.sourceId === selectedSourceId) ?? null

  // A source is "selected" if it exists in the list (canonical) or was surfaced by a search.
  const name = listSource?.name ?? matched?.sourceName ?? null
  const chunks = matched?.chunks ?? []

  return (
    <aside className="inspector insp" aria-label={t('i_source')}>
      {selectedSourceId && name ? (
        <>
          <div className="insp-doc">
            <span className="ftype md" aria-hidden="true">◈</span>
            <div className="dn">
              <b>{name}</b>
              <small>{selectedSourceId}</small>
            </div>
          </div>

          {listSource && (
            <>
              <h5>{t('i_details')}</h5>
              <dl className="insp-meta">
                <div>
                  <dt>{t('i_cat')}</dt>
                  <dd>
                    {listSource.category
                      ? <span className="badge cat">{listSource.category}</span>
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt>{t('i_format')}</dt>
                  <dd>{listSource.mime ?? '—'}</dd>
                </div>
                <div>
                  <dt>{t('s_chunks')}</dt>
                  <dd>{listSource.chunks}</dd>
                </div>
                <div>
                  <dt>{t('i_uploaded_by')}</dt>
                  <dd>{listSource.uploadedBy ?? '—'}</dd>
                </div>
                <div>
                  <dt>{t('i_uploaded_at')}</dt>
                  <dd>{formatUploaded(listSource.uploadedAt)}</dd>
                </div>
              </dl>
            </>
          )}

          {submittedQuery.length > 0 && (
            <>
              <h5>{t('i_chunks')}</h5>
              {chunks.length === 0 ? (
                <p className="kb-empty">—</p>
              ) : (
                <ul className="insp-chunks">
                  {chunks.map((c, i) => (
                    <li key={i} className="insp-chunk">{c}</li>
                  ))}
                </ul>
              )}
            </>
          )}

          <div className="note">{t('i_acl_note')}</div>
        </>
      ) : (
        <div className="insp-empty">
          <b>{t('i_no_selection')}</b>
          <p>{t('i_select_prompt')}</p>
        </div>
      )}
    </aside>
  )
}
